<template>
  <div :class="b('', { showMainImage })">
    <h3
      v-if="bundle.name"
      :class="b('name')"
    >
      {{ bundle.name }}
    </h3>
    <a
      :href="bundleUrl"
      :class="b('bundle-link')"
      :aria-label="mainImage?.alt || $t('c-product-bundle.link-alt-tag')"
    >
      <e-picture
        v-if="showMainImage"
        :srcset="mainImage?.srcset"
        :alt="mainImage?.alt || mainImage?.title || bundle.name"
        :title="mainImage?.title"
        :fallback="mainImage?.fallback"
        :ratio="1"
        :sizes="pictureSizes"
        :placeholder="!mainImage"
        loading="lazy"
        :class="b('image')"
        role="img"
      />
    </a>
    <div :class="b('collections-wrapper')">
      <div
        v-if="showMainImage"
        :class="b('collections-container-prefix')"
      >
        <e-icon
          icon="i-equal"
          size="20"
        />
      </div>

      <div
        ref="container"
        :class="b('collections-container', { isExpanded })"
      >
        <div
          v-for="(collection, index) in bundleConfig.collections"
          :key="`collection-${index}`"
          :class="b('collection-item')"
        >
          <div
            v-if="index > 0"
            :class="b('group-prefix')"
          >
            <e-icon
              icon="i-plus"
              size="16"
            />
          </div>
          <div
            v-for="(groupItem, groupIndex) in collection.groups"
            :key="`group-${groupIndex}`"
            ref="groupItems"
            :class="b('group-item-container', { firstChild: groupIndex === 0 })"
          >
            <div
              v-if="groupIndex > 0"
              :class="b('group-item-operator')"
            >
              <e-icon
                icon="i-arrow-split"
                size="16"
              />
            </div>
            <button
              type="button"
              :class="b('group-item', { highlight: groupItem.id === highlightId })"
              @click="onFetchVariants(groupItem)"
            >
              <e-picture
                :srcset="groupItem.mainImage?.srcset"
                :alt="groupItem.mainImage?.alt || groupItem.mainImage?.title || getGroupItemLabel(groupItem)"
                :title="groupItem.mainImage?.title"
                :fallback="groupItem.mainImage?.fallback"
                :ratio="1"
                :sizes="pictureSizes"
                :placeholder="!groupItem.mainImage"
                loading="lazy"
                :class="b('group-item-image')"
              />
              <span :class="b('group-item-label-wrapper')">
                {{ getGroupItemLabel(groupItem) }}
              </span>

              <e-loading-indicator
                v-if="selectedGroupItem?.id === groupItem.id && productStore.runningRequests.apiFetchProductVariants"
                :class="b('group-item-loading')"
                fullwidth
              />
            </button>
          </div>
        </div>
      </div>
      <button
        v-if="isMultiline"
        :class="b('button-show-more', { isExpanded, hide: !showToggleButton })"
        type="button"
        :aria-expanded="isExpanded"
        aria-controls="collections-container"
        @click="toggleExpanded"
      >
        {{ isExpanded ? $t('c-product-bundle.button-show-less') : $t('c-product-bundle.button-show-more') }}
        <e-icon
          :icon="isExpanded ? 'i-chevron--up' : 'i-chevron--down'"
          :size="isExpanded ? '14' : '18'"
        />
      </button>
    </div>

    <c-modal
      v-model:is-open="showOverlay"
      size="1024"
      :title="selectedGroupItem?.name || $t('c-product-bundle.modal-header')"
      close-on-outside-click
      @close="onCloseOverlay"
    >
      <div
        v-if="variants?.length"
        :class="b('modal-variants')"
      >
        <c-product-variant
          v-for="variant in variants"
          :key="variant.id"
          :product="variant"
          :highlight="variant.id === highlightId"
        />
      </div>
    </c-modal>
  </div>
</template>

<script lang="ts">
  import { PropType, Ref, defineComponent, ref } from 'vue';
  import { PRODUCT_DISPLAY_PICTURE_SIZES } from '@/setup/globals';
  import useProductStore from '@/stores/product';
  import cModal from '@/components/c-modal.vue';
  import cProductVariant from '@/components/c-product-variant.vue';
  import eIcon from '@/elements/e-icon.vue';
  import eLoadingIndicator from '@/elements/e-loading-indicator.vue';
  import ePicture from '@/elements/e-picture.vue';
  import { HtmlElement } from '@/types/html';
  import { Image } from '@/types/image';
  import { ProductBase, ProductBundle, ProductBundleDetails, ProductGroup } from '@/types/product';
  import { ImageSizes } from '@/types/sizes';

  type Setup = {
    productStore: ReturnType<typeof useProductStore>;
    container: Ref<HtmlElement>;
    groupItems: Ref<HtmlElement[] | null | undefined>;
  };

  type Data = {
    isMultiline: boolean;
    isExpanded: boolean;
    showToggleButton: boolean;
    showOverlay: boolean;
    selectedGroupItem: ProductGroup | null;
    variants: ProductBase[] | null;
    pictureSizes: ImageSizes;
  };

  /**
   * Renders a product bundle and the included collections and groups.
   */
  export default defineComponent({
    name: 'c-product-bundle',
    components: {
      eLoadingIndicator,
      cProductVariant,
      cModal,
      eIcon,
      ePicture,
    },

    props: {
      /**
       * The product to be displayed.
       */
      bundleConfig: {
        type: Object as PropType<ProductBundle>,
        required: true,
      },

      /**
       * The item with the given id will be highlighted.
       */
      highlightId: {
        type: Number,
        default: null,
      },

      /**
       * Whether to show the mainImage or not
       * We do not show it f.e. when using this component on the bundle detail page
       */
      showMainImage: {
        type: Boolean,
        default: true,
      },
    },
    // emits: {},

    setup(): Setup {
      return {
        productStore: useProductStore(),
        container: ref(),
        groupItems: ref(),
      };
    },
    data(): Data {
      return {
        isMultiline: false,
        isExpanded: false,
        showToggleButton: true,
        showOverlay: false,
        selectedGroupItem: null,
        variants: null,
        pictureSizes: PRODUCT_DISPLAY_PICTURE_SIZES,
      };
    },

    computed: {
      bundle(): ProductBundleDetails {
        return this.bundleConfig.bundle;
      },

      mainImage(): Image | null {
        return this.bundle?.mainImage;
      },

      bundleUrl(): string {
        return this.bundle.url || '';
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      this.checkIfContainerIsMultiline();
      window.addEventListener('resizeend', this.checkIfContainerIsMultiline);
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      window.removeEventListener('resizeend', this.checkIfContainerIsMultiline);
    },
    // unmounted() {},

    methods: {
      toggleExpanded() {
        this.isExpanded = !this.isExpanded;
        this.showToggleButton = false;

        setTimeout(() => {
          this.showToggleButton = true;
        }, 175);
      },

      checkIfContainerIsMultiline() {
        if (!this.container || !this.groupItems?.length) {
          return;
        }

        this.isMultiline = this.container.scrollHeight >= (this.groupItems[0]?.offsetHeight || 0) * 2;
      },

      onCloseOverlay() {
        this.selectedGroupItem = null;
        this.variants = null;
        this.showOverlay = false;
      },

      onFetchVariants(item: ProductGroup) {
        if (this.selectedGroupItem) {
          return;
        }

        this.selectedGroupItem = item;

        this.productStore
          .apiFetchProductVariants(item.id)
          .then((variants) => {
            if (variants.length === 1 && variants[0]?.url) {
              window.location.href = variants[0]?.url;
            } else if (variants?.length > 1) {
              this.showOverlay = true;
              this.variants = variants;
            } else {
              this.selectedGroupItem = null;
            }
          })
          .catch(() => {
            this.selectedGroupItem = null;
          });
      },

      getGroupItemLabel(groupItem: ProductGroup): string {
        return groupItem.bundleGroupItemLabel || this.$t('c-product-bundle.group-details');
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/setup/headings';

  @mixin icon-divider(
    $size,
    $container-padding,
    $border-color,
    $background-color: variables.$va-color-grayscale--1000
  ) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: calc($size - $container-padding / 2) * -1;
    width: $size;
    height: $size;
    border: 4px solid $border-color;
    background-color: $background-color;
    border-radius: 100%;
  }

  .c-product-bundle {
    $this: &;
    $background-collection-item: variables.$va-color-grayscale--900;
    $container-padding: 1rem;

    display: grid;
    grid-template-areas:
      'name name'
      'options options';
    grid-template-columns: calc(var(--theme-image-size--lg) + 2rem) 1fr;
    gap: $container-padding;
    padding-bottom: 2rem;
    border-bottom: 1px solid variables.$va-color-grayscale--900;

    &--show-main-image {
      grid-template-areas:
        'image name'
        'options options';

      @include mixins.media(md) {
        grid-template-areas:
          'name name'
          'image options';
      }

      &__collections-container {
        width: 100%;
      }
    }

    &__name {
      @extend .headline-4;

      grid-area: name;
    }

    &__image {
      grid-area: image;
      height: 100%;
      width: calc(var(--theme-image-size--lg) + 2rem);
      background-color: var(--theme-color-grayscale--950);
      padding: $container-padding;
      display: flex;
      align-items: center;
    }

    &__collections-wrapper {
      grid-area: options;
      position: relative;
    }

    &__collections-container {
      position: relative;
      background-color: var(--theme-color-grayscale--950);
      display: flex;
      flex-wrap: wrap;
      padding: $container-padding 2rem;
      gap: $container-padding;
      min-height: var(--theme-image-size--lg);
      max-height: calc(var(--theme-image-size--lg) + 2rem);
      overflow: hidden;
      transition: max-height 200ms ease-in-out;
      width: fit-content;
      z-index: 1;

      &--is-expanded {
        max-height: 100%;
      }
    }

    &__collections-container-prefix {
      @include icon-divider(2.2rem, $container-padding, var(--theme-color-grayscale--950));

      top: calc(50% - 1.1rem);
      z-index: 2;

      @include mixins.media($down: md) {
        left: -1rem;
        top: calc(100px - 1.1rem);
      }
    }

    &__collection-item {
      position: relative;
      display: flex;
      flex-wrap: wrap;
      align-items: stretch;
      border: 1px solid variables.$va-color-grayscale--800;
      background-color: $background-collection-item;
      padding: $container-padding;
      transition: border-color 350ms ease-in-out;
      border-radius: 3px;
      gap: 2rem 0;
    }

    &__collection-item:hover {
      border-color: variables.$va-color-grayscale--300;

      #{$this}__group-item-operator-label {
        color: var(--theme-color-grayscale--0);
      }
    }

    &__group-prefix {
      @include icon-divider(2.2rem, $container-padding, $background-collection-item);

      align-self: center;
    }

    &__group-item-container {
      position: relative;
      display: flex;
      align-items: flex-start;
      margin-left: $container-padding;

      &--first-child {
        margin-left: 0;
      }
    }

    &__group-item {
      @include mixins.hover-effect--default;

      position: relative;
      display: flex;
      flex-direction: column;
      width: 75px;
      height: 100%;
      min-height: 8rem;
      background-color: variables.$va-color-grayscale--1000;
      padding: 0.6rem;
      gap: 0.5rem;

      @include mixins.media(sm) {
        width: 6.5rem;
      }

      &:first-child {
        margin-left: 0;
      }

      &--highlight {
        border: 3px solid variables.$va-color-grayscale--0;
      }
    }

    &__group-item-label-wrapper {
      @include mixins.line-clamp(3, 1.2);
      @include mixins.hyphens;

      font-size: variables.$font-size--smallest;
      line-height: 1.2em;
      text-align: center;
    }

    &__group-item-operator {
      @include icon-divider(2rem, $container-padding, $background-collection-item);

      align-self: center;
      z-index: 2;
    }

    &__button-show-more {
      $button-show-more-transition: 350ms ease-in-out;

      @include mixins.hover-effect--default;

      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      bottom: 0;
      background-color: rgba(var(--theme-color-grayscale--0-rgb), 0.6);
      color: variables.$va-color-grayscale--1000;
      font-size: variables.$font-size--small;
      width: 100%;
      text-align: center;
      height: 2.4rem;
      padding: 0.4rem;
      transition:
        height $button-show-more-transition,
        font-size $button-show-more-transition;
      transition-delay: 50ms;
      opacity: 1;
      gap: 0.5rem;
      z-index: 2;

      &--hide {
        opacity: 0;
      }

      &--is-expanded {
        bottom: -2rem;
        height: 2rem;
        font-size: variables.$font-size--smallest;
      }
    }

    &__modal-variants {
      --max-items: 1;

      display: grid;
      gap: 3rem 1.5rem;
      grid-template-columns: repeat(
        auto-fit,
        minmax(calc(100% / var(--max-items) - (1.5rem * (var(--max-items) - 1) / var(--max-items))), 1fr)
      );

      @include mixins.media(sm) {
        --max-items: 2;
      }
    }
  }
</style>
